import { Modal } from '@medeloop/medeloop-components-library';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UpdateUserDTO } from 'src/api/users';
import { useUpdateUserMutation, useVerifyUserAttribute } from 'src/api/users-queries';
import { MfaCodeInput } from '../mfa-code-input/mfa-code-input';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css'
import { isValidPhoneNumber } from 'react-phone-number-input'

interface UpdatePhoneNumberModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (isOpen: boolean) => void;
  userProfile: UpdateUserDTO;
}

export const UpdatePhoneNumberModal = ({
  isModalOpen,
  setIsModalOpen,
  userProfile,
}: UpdatePhoneNumberModalProps) => {
  const { t } = useTranslation();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [isPhoneNumberValid, setIsPhoneNumberValid] = useState(false);
  const [verficationCodeSent, setVerificationCodeSent] = useState(false);
  const [code, setCode] = useState(['', '', '', '', '', '']);

  const updateUserMutation = useUpdateUserMutation({ showConfirmationToast: false });
  const verifyUserAttributeMutation = useVerifyUserAttribute('phone_number');

  const closeAndResetChangeEmailModal = () => {
    setIsModalOpen(false);
    setVerificationCodeSent(false);
    setIsPhoneNumberValid(true);
    setPhoneNumber('');
    setCode(['', '', '', '', '', '']);
  };

  const handleUpdateEmail = async () => {
    if (verficationCodeSent) {
      const verificationCode = code.join('');
      await verifyUserAttributeMutation.mutate({ code: verificationCode });
    } else {
      await updateUserMutation.mutate({
        ...userProfile,
        phoneNumber: phoneNumber,
      });
      setVerificationCodeSent(true);
    }
  };

  const handlePhoneNumerChange = (phoneNumber: string | undefined) => {
    if (phoneNumber !== undefined) {
      if (isValidPhoneNumber(phoneNumber)) {
        setIsPhoneNumberValid(true);
      } else {
        setIsPhoneNumberValid(false);
      }
      setPhoneNumber(phoneNumber);
    }
  };

  useEffect(() => {
    if (verifyUserAttributeMutation.isSuccess) {
      closeAndResetChangeEmailModal();
    }
  }, [verifyUserAttributeMutation.isSuccess]);

  return (
    <Modal
      title={verficationCodeSent ? 'Enter the verification code' : 'Change phone number'}
      isOpen={isModalOpen}
      actionType="primary"
      onCancel={() => closeAndResetChangeEmailModal()}
      actionText={verficationCodeSent ? 'Change phone' : 'Next'}
      cancelText={t('cancel')}
      actionFunction={handleUpdateEmail}
      disabled={!isPhoneNumberValid}
    >
      <div className="flex flex-col gap-4 w-[432px] text-sm text-content-on-dark-default">
        {verficationCodeSent ? (
          <>
            <span>
              We’ve sent a 6-digit verification code to your new phone. Please enter it below to
              confirm your new phone number.
            </span>
            <MfaCodeInput code={code} setCode={setCode} evaluateMFA={handleUpdateEmail} />
            {verifyUserAttributeMutation.isError && (
              <span className="text-red-500 text-sm">The code inserted was incorrect</span>
            )}
          </>
        ) : (
          <>
            <span>
              We’ll send a verification code to the new phone number. This change will apply across
              all Medeloop platforms you’re part of.
            </span>
            <div className='border border-cool-gray-600 bg-dark-subdued-background rounded-md p-2 focus-within:ring-[1px] focus-within:ring-cyan-300'>
              <PhoneInput
                className="text-content-on-dark-default text-sm [&_.PhoneInputInput]:bg-dark-subdued-background [&_.PhoneInputInput]:outline-none [&_.PhoneInputInput]:border-0"
                defaultCountry="US"
                international
                placeholder="Enter phone number"
                value={phoneNumber}
                onChange={(pn) => handlePhoneNumerChange(pn)}
              />
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
